import React, { useState, useEffect } from "react";
import { X, User, Phone, Mail, IndianRupee, MapPin } from "lucide-react";
import { toast } from "react-toastify";

const loanOptions = [
  "Personal Loan",
  "Business Loan",
  "Loan Against Property",
  "Gold Loan",
  "EV Loan",
];

const sanitizeLoanType = (type = "") => {
  if (loanOptions.includes(type)) return type;
  const lower = String(type).toLowerCase();
  if (lower.includes("business")) return "Business Loan";
  if (lower.includes("gold")) return "Gold Loan";
  if (lower.includes("ev") || lower.includes("vehicle") || lower.includes("car")) return "EV Loan";
  if (lower.includes("property") || lower.includes("stamp") || lower.includes("home") || lower.includes("lap")) {
    return "Loan Against Property";
  }
  return "Personal Loan";
};

function ApplyLoanModal({ isOpen, onClose, onSuccess, defaultLoanType = "Personal Loan" }) {
  const [formData, setFormData] = useState({
    fullName: "",
    mobile: "",
    email: "",
    loanType: sanitizeLoanType(defaultLoanType),
    loanAmount: "",
    city: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync loanType whenever defaultLoanType prop changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        loanType: sanitizeLoanType(defaultLoanType),
      }));
      setIsSubmitting(false);
    }
  }, [isOpen, defaultLoanType]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);

      // 1. Trigger Toast Alert
      toast.success("Application submitted successfully!", {
        position: "top-right",
        autoClose: 3500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // 2. Trigger onSuccess callback to close apply modal and open SuccessModal
      if (onSuccess) {
        onSuccess(formData);
      } else {
        onClose();
      }
    }, 450);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/65 backdrop-blur-sm transition-opacity duration-300 overflow-x-hidden overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[490px] max-h-[90vh] overflow-y-auto overflow-x-hidden rounded-[22px] sm:rounded-[28px] border border-[#d8e8de] bg-white p-4 sm:p-6 md:p-8 shadow-[0_25px_70px_rgba(0,0,0,0.25)] animate-modalPop"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Contained Soft Background Mint Light Aura (No Horizontal Overflow) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[22px] sm:rounded-[28px]"
        >
          <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(46,229,162,0.2),transparent_70%)] blur-xl" />
        </div>

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute right-3.5 top-3.5 sm:right-5 sm:top-5 z-20 flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-[#f3f4f6] text-[#4b5563] transition-all hover:bg-[var(--hero-green)] hover:text-white cursor-pointer"
        >
          <X size={17} strokeWidth={2.4} />
        </button>

        {/* Header */}
        <div className="pr-8 relative z-10">
          <span className="inline-block rounded-full bg-[var(--hero-green-light)] px-2.5 py-0.5 font-jakarta text-[11px] sm:text-[12px] font-bold text-[var(--hero-green)] border border-[var(--hero-green-border)]">
            Quick Application
          </span>

          <h3 className="mt-2 font-bricolage text-[20px] sm:text-[25px] font-bold leading-tight tracking-tight text-[#111827]">
            Apply for{" "}
            <span className="text-[var(--hero-green)]">{formData.loanType}</span>
          </h3>

          <p className="mt-1 font-jakarta text-[12px] sm:text-[13px] text-[#6b7280]">
            Fill in your details for instant pre-eligibility review.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-4 sm:mt-5 space-y-3 sm:space-y-3.5 relative z-10 w-full">
          
          {/* Full Name */}
          <div className="w-full min-w-0">
            <label className="block font-jakarta text-[12px] sm:text-[13px] font-semibold text-[#374151] mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative w-full">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9ca3af]" />
              <input
                type="text"
                name="fullName"
                required
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full box-border rounded-xl border border-[#d1d5db] bg-[#f9fafb] py-2 sm:py-2.5 pl-9 pr-3.5 font-jakarta text-[13px] sm:text-[14px] text-[#111827] outline-none transition-all focus:border-[var(--hero-green)] focus:bg-white focus:ring-2 focus:ring-[var(--hero-green)]/20"
              />
            </div>
          </div>

          {/* Mobile & Email in 2 columns */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3.5 w-full">
            {/* Mobile */}
            <div className="w-full min-w-0">
              <label className="block font-jakarta text-[12px] sm:text-[13px] font-semibold text-[#374151] mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <div className="relative w-full">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9ca3af]" />
                <input
                  type="tel"
                  name="mobile"
                  required
                  pattern="[0-9]{10}"
                  placeholder="10-digit mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="w-full box-border rounded-xl border border-[#d1d5db] bg-[#f9fafb] py-2 sm:py-2.5 pl-9 pr-3.5 font-jakarta text-[13px] sm:text-[14px] text-[#111827] outline-none transition-all focus:border-[var(--hero-green)] focus:bg-white focus:ring-2 focus:ring-[var(--hero-green)]/20"
                />
              </div>
            </div>

            {/* Email */}
            <div className="w-full min-w-0">
              <label className="block font-jakarta text-[12px] sm:text-[13px] font-semibold text-[#374151] mb-1">
                Email Address
              </label>
              <div className="relative w-full">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9ca3af]" />
                <input
                  type="email"
                  name="email"
                  placeholder="name@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full box-border rounded-xl border border-[#d1d5db] bg-[#f9fafb] py-2 sm:py-2.5 pl-9 pr-3.5 font-jakarta text-[13px] sm:text-[14px] text-[#111827] outline-none transition-all focus:border-[var(--hero-green)] focus:bg-white focus:ring-2 focus:ring-[var(--hero-green)]/20"
                />
              </div>
            </div>
          </div>

          {/* Loan Product & Loan Amount */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3.5 w-full">
            {/* Loan Type */}
            <div className="w-full min-w-0">
              <label className="block font-jakarta text-[12px] sm:text-[13px] font-semibold text-[#374151] mb-1">
                Loan Product <span className="text-red-500">*</span>
              </label>
              <select
                name="loanType"
                value={formData.loanType}
                onChange={handleChange}
                className="w-full box-border rounded-xl border border-[#d1d5db] bg-[#f9fafb] py-2 sm:py-2.5 px-3 font-jakarta text-[13px] sm:text-[14px] text-[#111827] outline-none transition-all focus:border-[var(--hero-green)] focus:bg-white focus:ring-2 focus:ring-[var(--hero-green)]/20 cursor-pointer"
              >
                {loanOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Loan Amount */}
            <div className="w-full min-w-0">
              <label className="block font-jakarta text-[12px] sm:text-[13px] font-semibold text-[#374151] mb-1">
                Required Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative w-full">
                <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9ca3af]" />
                <input
                  type="text"
                  name="loanAmount"
                  required
                  placeholder="e.g. 5,00,000"
                  value={formData.loanAmount}
                  onChange={handleChange}
                  className="w-full box-border rounded-xl border border-[#d1d5db] bg-[#f9fafb] py-2 sm:py-2.5 pl-9 pr-3.5 font-jakarta text-[13px] sm:text-[14px] text-[#111827] outline-none transition-all focus:border-[var(--hero-green)] focus:bg-white focus:ring-2 focus:ring-[var(--hero-green)]/20"
                />
              </div>
            </div>
          </div>

          {/* City / Location */}
          <div className="w-full min-w-0">
            <label className="block font-jakarta text-[12px] sm:text-[13px] font-semibold text-[#374151] mb-1">
              City / Location <span className="text-red-500">*</span>
            </label>
            <div className="relative w-full">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9ca3af]" />
              <input
                type="text"
                name="city"
                required
                placeholder="Enter city (e.g. Mumbai, Delhi, Bengaluru)"
                value={formData.city}
                onChange={handleChange}
                className="w-full box-border rounded-xl border border-[#d1d5db] bg-[#f9fafb] py-2 sm:py-2.5 pl-9 pr-3.5 font-jakarta text-[13px] sm:text-[14px] text-[#111827] outline-none transition-all focus:border-[var(--hero-green)] focus:bg-white focus:ring-2 focus:ring-[var(--hero-green)]/20"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-1.5 sm:pt-2 w-full">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--hero-green)] py-3 sm:py-3.5 font-jakarta text-[14px] sm:text-[15px] font-bold text-white shadow-md transition-all hover:bg-[var(--hero-green-hover)] hover:-translate-y-0.5 cursor-pointer disabled:opacity-75"
            >
              {isSubmitting ? "Processing..." : "Submit Application →"}
            </button>
          </div>

          {/* Disclaimer */}
          <p className="text-center font-jakarta text-[11px] text-[#9ca3af]">
            🔒 Data encrypted with 256-bit bank-grade security.
          </p>
        </form>
      </div>

      {/* Modal Pop Animation CSS */}
      <style>{`
        @keyframes modalPop {
          0% {
            opacity: 0;
            transform: scale(0.95) translateY(8px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-modalPop {
          animation: modalPop 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
}

export default ApplyLoanModal;
